import styled from '@emotion/styled';
import type { RadioProps } from 'antd';
import { Radio } from 'antd';
import type { RadioButtonProps } from 'antd/es/radio/radioButton';
import React from 'react';

import Ellipsis from './Ellipsis';
import RadioMedia from './RadioMedia';

/**
 * Adds nothing, but cannot be replaced by antd's `Radio`: `radio/index.js` does
 * `Radio.Group = Group; Radio.Button = Button`, so attaching our namespace to it would clobber
 * those for the call sites importing `Radio` from `antd` directly.
 */
const FBRadio: React.FC<RadioProps> = props => <Radio {...props} />;

/**
 * The height comes from `Radio.controlHeight`: the height/padding/line-height triple that used to be
 * here summed to the same 32px while blocking `size` from scaling. The inline padding is also a
 * token (`Radio.buttonPaddingInline`); only the flex centring and the block-variant ellipsis stay.
 */
const RadioGroup = styled(Radio.Group)`
  display: flex;

  &.ant-radio-group-block {
    overflow: hidden;
    flex-wrap: wrap;

    > .ant-radio-button-wrapper-block {
      overflow: hidden;

      > .ant-radio-button-label {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }

  .ant-radio-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;

    @media screen and (max-width: 767px) {
      padding-inline: 0;
    }

    > span:last-child {
      white-space: nowrap;
    }
  }
`;

const RadioButton: React.FC<RadioButtonProps> = ({ children, ...rest }) => (
  <Radio.Button {...rest}>
    <Ellipsis clamp={1}>{children}</Ellipsis>
  </Radio.Button>
);

export default Object.assign(FBRadio, {
  Group: RadioGroup,
  Button: RadioButton,
  Media: RadioMedia,
});
